import { useState } from 'react';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material';
import { useToast } from 'contexts/ToastContext';
import sasLogo from 'assets/images/users/saslogo.png';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AuthWrapper from 'sections/auth/AuthWrapper';
import api from 'utils/api';
import { getDefaultPathForUser, setAuth } from 'utils/auth';
import { APP_DEFAULT_PATH } from 'config';

const fieldLabelSx = {
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: 'text.primary',
  mb: 0.75
};

const inputSx = {
  borderRadius: 2.5,
  bgcolor: '#fafcfb',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(31, 91, 64, 0.12)'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(31, 91, 64, 0.28)'
  },
  '&.Mui-focused': {
    bgcolor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: 1.5
    }
  }
};

export default function Login() {
  const navigate = useNavigate();
  const { showToast, showApiError } = useToast();

  const [email, setEmail] = useState('admin@test.com');
  const [password, setPassword] = useState('123456');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });

  const emailError =
    touched.email && (!email ? 'Email is required' : !/\S+@\S+\.\S+/.test(email) ? 'Enter a valid email' : '');

  const passError = touched.password && !password ? 'Password is required' : '';

  const isValid = email && /\S+@\S+\.\S+/.test(email) && password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!isValid) return;

    setLoading(true);

    try {
      const res = await api.post('/login', { email, password });

      if (res.data.status) {
        const { token, user } = res.data.data;
        setAuth(token, user);
        navigate(getDefaultPathForUser(user) || APP_DEFAULT_PATH, { replace: true });
      } else {
        showToast(res.data.message || 'Login failed.');
      }
    } catch (error) {
      console.error('Login error:', error);
      showApiError(error, 'Network error or server unavailable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthWrapper>
      <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
        {/* Brand header */}
        <Stack alignItems="center" spacing={1} sx={{ width: '100%', textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: '#1f5b40',
              background: 'linear-gradient(145deg, #3da872 0%, #1f5b40 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(31, 91, 64, 0.28)',
              flexShrink: 0
            }}
          >
            <Box
              component="img"
              src={sasLogo}
              alt="Logo"
              sx={{ width: 48, height: 48, objectFit: 'contain' }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              color: 'text.primary'
            }}
          >
            Sign in
          </Typography>
        </Stack>

        <Box
          sx={{
            width: '100%',
            height: '1px',
            bgcolor: 'rgba(31, 91, 64, 0.08)'
          }}
        />

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ width: '100%' }}
        >
          <Stack spacing={2}>
            <Box>
              <InputLabel htmlFor="email" sx={fieldLabelSx}>
                Email address
              </InputLabel>
              <OutlinedInput
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                placeholder="you@example.com"
                fullWidth
                error={Boolean(emailError)}
                sx={inputSx}
                inputProps={{ autoComplete: 'email' }}
              />
              {emailError && <FormHelperText error sx={{ mt: 0.75, mx: 0 }}>{emailError}</FormHelperText>}
            </Box>

            <Box>
              <InputLabel htmlFor="password" sx={fieldLabelSx}>
                Password
              </InputLabel>
              <OutlinedInput
                id="password"
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                placeholder="Enter your password"
                fullWidth
                error={Boolean(passError)}
                sx={inputSx}
                inputProps={{
                  autoComplete: 'current-password',
                  name: 'password'
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPass((v) => !v)}
                      edge="end"
                      color="secondary"
                      aria-label="toggle password visibility"
                      sx={{ mr: -0.5 }}
                    >
                      {showPass ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {passError && <FormHelperText error sx={{ mt: 0.75, mx: 0 }}>{passError}</FormHelperText>}
            </Box>

            <Button
              fullWidth
              size="medium"
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                mt: 0.25,
                py: 1.1,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(31, 91, 64, 0.25)',
                '&:hover': {
                  boxShadow: '0 6px 18px rgba(31, 91, 64, 0.32)'
                }
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </AuthWrapper>
  );
}
